import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCcw,
  Trash2,
  Mail,
  Phone,
  Building2,
  Search,
  Inbox,
  X,
  ExternalLink,
  MessageSquare,
  Briefcase,
  FileText,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { auth, authApi, formatApiError } from "@/lib/auth";
import JobsManager from "@/pages/JobsManager";
import ApplicationsManager from "@/pages/ApplicationsManager";

function fmtDate(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [tab, setTab] = useState("inquiries");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authApi.get("/admin/inquiries");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const t = q.toLowerCase();
    return items.filter((i) =>
      [i.name, i.email, i.company, i.service_interest, i.message, i.phone]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(t))
    );
  }, [items, q]);

  const stats = useMemo(() => {
    const now = new Date();
    const in7d = items.filter((i) => (now - new Date(i.created_at)) / 86400000 <= 7).length;
    const in30d = items.filter((i) => (now - new Date(i.created_at)) / 86400000 <= 30).length;
    return { total: items.length, week: in7d, month: in30d };
  }, [items]);

  if (!auth.isAuthed()) return <Navigate to="/admincontrol/login" replace />;

  const logout = () => {
    auth.logout();
    nav("/admincontrol/login", { replace: true });
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await authApi.delete(`/admin/inquiries/${toDelete.id}`);
      setItems((prev) => prev.filter((i) => i.id !== toDelete.id));
      if (selected?.id === toDelete.id) setSelected(null);
      toast.success("Inquiry deleted.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed.");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-bone">
      {/* Top bar */}
      <header className="bg-white border-b border-hairline sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-4">
          <Link to="/admincontrol" className="flex items-center gap-3">
            <img src="/brand/logo-mark.png" alt="Novacore" className="w-8 h-8 object-contain" />
            <div className="leading-none">
              <div className="font-display text-base font-semibold text-ink">Novacore Console</div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-muted2 mt-0.5">
                Admin dashboard
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline text-sm text-muted2" data-testid="admin-user-email">
              {auth.email()}
            </span>
            <Link
              to="/"
              data-testid="admin-view-site"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-forest hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink className="w-4 h-4" /> View site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm text-ink border border-hairline px-4 py-2 hover:bg-ink hover:text-bone transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-hairline overflow-x-auto">
          {[
            { id: "inquiries", label: "Inquiries", icon: MessageSquare },
            { id: "applications", label: "Applications", icon: FileText },
            { id: "jobs", label: "Jobs", icon: Briefcase },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  active
                    ? "border-forest text-forest"
                    : "border-transparent text-muted2 hover:text-ink"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "jobs" && <JobsManager />}
        {tab === "applications" && <ApplicationsManager />}

        {tab === "inquiries" && (
        <>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { k: stats.total, v: "Total inquiries", testid: "stat-total" },
            { k: stats.month, v: "Last 30 days", testid: "stat-30d" },
            { k: stats.week, v: "Last 7 days", testid: "stat-7d" },
          ].map((s) => (
            <div
              key={s.v}
              data-testid={s.testid}
              className="bg-white border border-hairline p-6"
            >
              <div className="font-display text-4xl font-medium text-forest">{s.k}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted2 mt-2">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, company, message..."
              data-testid="admin-search"
              className="pl-9 h-11 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
            />
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 text-sm text-ink border border-hairline px-4 py-2.5 hover:bg-ink hover:text-bone transition-colors self-start"
          >
            <RefreshCcw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-hairline overflow-hidden">
          {loading ? (
            <div data-testid="admin-loading" className="p-12 text-center text-muted2">Loading…</div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty" className="p-16 text-center">
              <Inbox className="w-10 h-10 mx-auto text-muted2 mb-4" strokeWidth={1.3} />
              <div className="font-display text-xl text-ink">No inquiries yet</div>
              <p className="text-sm text-muted2 mt-2">
                {items.length === 0
                  ? "New submissions from your contact form will appear here."
                  : "No results match your search."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-bone border-b border-hairline">
                  <tr className="text-left">
                    <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Name</th>
                    <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Company</th>
                    <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Service</th>
                    <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Received</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((i) => (
                    <tr
                      key={i.id}
                      data-testid={`row-${i.id}`}
                      className="border-b border-hairline last:border-b-0 hover:bg-bone/60 cursor-pointer"
                      onClick={() => setSelected(i)}
                    >
                      <td className="px-5 py-4">
                        <div className="font-medium text-ink">{i.name}</div>
                        <div className="text-xs text-muted2 mt-0.5">{i.email}</div>
                      </td>
                      <td className="px-5 py-4 text-ink/80">{i.company || "—"}</td>
                      <td className="px-5 py-4">
                        {i.service_interest ? (
                          <span className="inline-block px-2 py-0.5 text-xs bg-forest/8 text-forest border border-forest/15">
                            {i.service_interest}
                          </span>
                        ) : (
                          <span className="text-muted2">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-muted2 whitespace-nowrap">{fmtDate(i.created_at)}</td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setToDelete(i);
                          }}
                          data-testid={`delete-${i.id}`}
                          className="text-muted2 hover:text-red-600 p-2"
                          aria-label="Delete inquiry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid="inquiry-drawer"
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div className="absolute inset-0 bg-ink/40" />
          <div
            className="relative w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-hairline flex items-center justify-between">
              <div>
                <div className="overline text-terracotta">Inquiry</div>
                <h3 className="font-display text-2xl font-medium text-ink mt-1">{selected.name}</h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="drawer-close"
                className="p-2 text-muted2 hover:text-ink"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5 text-sm">
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-forest mt-1 shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-muted2">Email</div>
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-forest hover:underline"
                  >
                    {selected.email}
                  </a>
                </div>
              </div>
              {selected.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-forest mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted2">Phone</div>
                    <a href={`tel:${selected.phone}`} className="text-forest hover:underline">
                      {selected.phone}
                    </a>
                  </div>
                </div>
              )}
              {selected.company && (
                <div className="flex items-start gap-3">
                  <Building2 className="w-4 h-4 text-forest mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted2">Company</div>
                    <div className="text-ink">{selected.company}</div>
                  </div>
                </div>
              )}
              {selected.service_interest && (
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-muted2 mb-1">Service interest</div>
                  <span className="inline-block px-2 py-0.5 text-xs bg-forest/8 text-forest border border-forest/15">
                    {selected.service_interest}
                  </span>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted2 mb-2">Message</div>
                <p className="text-ink whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>
              <div className="text-xs text-muted2 pt-4 border-t border-hairline">
                Received {fmtDate(selected.created_at)}
              </div>
            </div>

            <div className="p-6 border-t border-hairline flex gap-3">
              <a
                href={`mailto:${selected.email}?subject=Re: your inquiry to Novacore Services`}
                data-testid="drawer-reply"
                className="btn-primary flex-1 justify-center"
              >
                <Mail className="w-4 h-4" /> Reply
              </a>
              <button
                onClick={() => setToDelete(selected)}
                data-testid="drawer-delete"
                className="btn-ghost"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the inquiry from{" "}
              <strong>{toDelete?.name}</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
